import { ComponentType, ReactNode, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import './CollapsibleNavItem.css';

/**
 * CollapsibleNavItem
 *
 * Renders a collapsible (accordion-like) navigation item using Framer Motion,
 * which smoothly expands/collapses to reveal nested `<li>` elements or other
 * sub-navigation items.
 */
type CollapsibleNavItemProps = {
  /**
   * The visible label/title for this collapsible section.
   */
  label: string;

  icon?: ComponentType<{ className?: string }>;

  defaultOpen?: boolean;

  /**
   * The child elements to be rendered within the collapsible section.
   * Typically, these are one or more `<li>` or other nested
   * `<CollapsibleNavItem>` elements.
   */
  children: ReactNode;
};

export function CollapsibleNavItem({
  label,
  icon: Icon,
  defaultOpen = false,
  children,
}: CollapsibleNavItemProps) {
  const [open, setOpen] = useState(defaultOpen);

  const handleToggle = () => {
    setOpen(!open);
  };

  const variants = {
    open: {
      height: 'auto',
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: 'easeInOut',
      },
    },
    collapsed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <li className="collapsible-nav-item">
      <button className="collapsible-nav-button" onClick={handleToggle}>
        <span className="inline-flex items-center gap-2.5">
          {Icon && <Icon className="size-[18px] shrink-0" />}
          {label}
        </span>
        <i className={open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={variants}
            style={{ overflow: 'hidden' }}
          >
            <div className="collapsible-nav-list">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  );
}
